import { useCallback, useEffect, useRef, useState } from 'react';

import { showErrorMessage } from '../../helpers/utils/ui';
import { importApi } from '../../shared/common/api/endpoints/importApi';
import {
    ExportTaskCreateRequest,
    ExportTasksFilterRequest,
    ExportTasksFilterResponse,
    InfoExportResponseData
} from './types';

export const enum FILTER_FIELDS {
    schoolId = 'school_id',
    sourceIds = 'source_ids',
    taskCreatedFrom = 'task_created_from',
    taskCreatedTo = 'task_created_to',
    exportCreatedFrom = 'export_created_from',
    exportCreatedTo = 'export_created_to',
    taskTypeIds = 'task_type_ids',
    creationTypeIds = 'creation_type_ids',
    createdByUserIds = 'created_by_user_ids',
    statusIds = 'status_ids',
    adCabinetIds = 'ad_cabinet_ids',
    page = 'page',
    pageSize = 'pageSize',
    sortField = 'sortField',
    sortDirection = 'sortDirection',
}

// Проверка ответа с кодом ошибки доступа (HTTP 200, но code: 221 в body)
const isAccessDeniedResponse = (data: any): boolean => {
    return data?.code === 221;
};

// Понятное сообщение об ошибке доступа
const ACCESS_DENIED_MESSAGE = 'У вас недостаточно прав для просмотра задач импорта. Обратитесь к владельцу школы для получения доступа.';

export const useExportTasks = (filters: ExportTasksFilterRequest) => {
    const [isLoading, setIsLoading] = useState(false);
    const [dictionaries, setDictionaries] = useState<InfoExportResponseData | null>(null);
    const [tasks, setTasks] = useState<ExportTasksFilterResponse | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);
    // Ref для проверки доступа внутри замыканий (state не обновляется в замыкании)
    const accessDeniedRef = useRef(false);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const isMountedRef = useRef(true);

    const markAccessDenied = () => {
        accessDeniedRef.current = true;
        setAccessDenied(true);
        showErrorMessage(ACCESS_DENIED_MESSAGE);
        setIsLoading(false);
    };

    const fetchData = async () => {
        // Если доступ запрещён — не делаем повторных запросов
        if (accessDeniedRef.current) return;

        setIsLoading(true);
        try {
            if (!dictionaries) {
                const dictionariesResponse = await importApi.getInfoExport();
                if (!isMountedRef.current) return;

                // Проверяем ответ с кодом ошибки доступа (HTTP 200, code: 221)
                if (isAccessDeniedResponse(dictionariesResponse.data)) {
                    markAccessDenied();
                    return;
                }

                setDictionaries(dictionariesResponse.data.data);
            }
        } catch (error: any) {
            if (!isMountedRef.current) return;
            const message = error?.response?.data?.message || error?.message || 'Ошибка загрузки справочников';
            showErrorMessage(message);
        }

        // Если доступ был запрещён на этапе справочников — не запрашиваем задачи
        if (accessDeniedRef.current) return;

        try {
            const tasksResponse = await importApi.getExportTasksInFilter(filters);
            if (!isMountedRef.current) return;

            // Проверяем ответ с кодом ошибки доступа (HTTP 200, code: 221)
            if (isAccessDeniedResponse(tasksResponse.data)) {
                markAccessDenied();
                return;
            }

            setTasks(tasksResponse.data);

            // Обновляем списки пользователей и рекламных кабинетов из ответа
            if (tasksResponse.data.available_users || tasksResponse.data.available_ad_cabinets) {
                setDictionaries(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        ...(tasksResponse.data.available_users && { users: tasksResponse.data.available_users }),
                        ...(tasksResponse.data.available_ad_cabinets && { ad_cabinets: tasksResponse.data.available_ad_cabinets })
                    };
                });
            }
        } catch (error: any) {
            if (!isMountedRef.current) return;
            const message = error?.response?.data?.message || error?.message || 'Ошибка загрузки задач';
            showErrorMessage(message);
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const postCreateTask = async (values: ExportTaskCreateRequest) => {
        return importApi.createExportTask(values);
    };

    const deleteTask = async (taskId: number) => {
        return importApi.deleteExportTask(taskId);
    };

    const fetchTasks = useCallback(() => {
        if (!accessDeniedRef.current) {
            fetchData();
        }
    }, [filters]);

    useEffect(() => {
        if (!accessDeniedRef.current) {
            fetchData();
        }
    }, [filters]);

    // Очистка при размонтировании — предотвращает ошибки на других страницах
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return {
        postCreateTask,
        deleteTask,
        fetchTasks,
        isLoading,
        tasks,
        dictionaries,
        accessDenied
    };
};
